// Brand Risk privacy - what personal information the mention feeds are
// allowed to show. Three groups of controls:
//   profile pictures  (show the real avatar, or a platform default disc)
//   author names      (real handle, or a masked label)
//   detected PII      (per-type reveal, redacted by default)
//
// Write access is org_admin or higher; the backend returns HTTP 403 to
// anyone else, and this UI mirrors that gate. The PII type list is served by
// the API, so a detector added on the backend appears here with no frontend
// change.
import React, { useEffect, useMemo, useState } from 'react';
import {
    bp_get_privacy_settings, bp_put_privacy_settings,
} from '../../../services';
import { formatLocalDateTime, unwrap, writeResult } from './_shared';
import CommonButton from '../../../Components/CommonButton';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonLoader from '../../../Components/CommonLoader';
import StatCard from '../../../Components/StatCard';
import StatusBadge from '../../../Components/StatusBadge';
import { isOrgAdminOrHigher } from '../../../helper';
import { Body, SubHeading } from '../../../Constants/Fonts';
import ModuleIcon from '../../../Components/ModuleIcon';

function Panel({ title, note, children }) {
    return (
        <section className="bm_prv_panel cmn_elevated_card">
            {(title || note) && (
                <div className="bm_prv_panel_head">
                    <div>
                        {title && (
                            <SubHeading as="h3" variant={2} className="bm_prv_panel_title">
                                {title}
                            </SubHeading>
                        )}
                        {note && (
                            <Body as="span" variant={2} className="bm_prv_muted">{note}</Body>
                        )}
                    </div>
                </div>
            )}
            {children}
        </section>
    );
}

function ToggleRow({ label, description, value, onChange, disabled,
                     onLabel = 'Visible', offLabel = 'Redacted', isLast = false }) {
    return (
        <div className={`bm_prv_toggle${isLast ? ' is-last' : ''}`}>
            <div className="bm_prv_toggle_info">
                <Body as="div" variant={1} weight="semibold" className="bm_prv_toggle_label">
                    {label}
                </Body>
                {description && (
                    <Body as="div" variant={2} className="bm_prv_toggle_desc">
                        {description}
                    </Body>
                )}
            </div>
            <CommonButton
                text={value ? onLabel : offLabel}
                variant={value ? 'white' : 'primary'}
                className="bm_scan_btn"
                onClick={() => onChange(!value)}
                disable={disabled}
            />
        </div>
    );
}

export default function PrivacySection() {
    const canManage = isOrgAdminOrHigher();
    const [cfg, setCfg] = useState(null);
    const [draft, setDraft] = useState(null);
    const [err, setErr] = useState(null);
    const [notice, setNotice] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => {
        bp_get_privacy_settings().then(r => {
            const d = unwrap(r) || {};
            setCfg(d);
            setDraft({
                show_avatar:    d.show_avatar,
                show_name:      d.show_name,
                show_source_url: d.show_source_url,
                name_mask_mode: d.name_mask_mode,
                pii_visibility: { ...(d.pii_visibility || {}) },
            });
        }).catch(() => setErr('Could not load the privacy policy.'));
    };

    useEffect(() => { load(); }, []);

    const dirty = useMemo(() => {
        if (!cfg || !draft) return false;
        return JSON.stringify({
            show_avatar:     cfg.show_avatar,
            show_name:       cfg.show_name,
            show_source_url: cfg.show_source_url,
            name_mask_mode:  cfg.name_mask_mode,
            pii_visibility:  cfg.pii_visibility || {},
        }) !== JSON.stringify(draft);
    }, [cfg, draft]);

    const piiTypes      = cfg?.available_pii_types || [];
    const revealedCount = draft
        ? Object.values(draft.pii_visibility || {}).filter(Boolean).length : 0;

    const save = async () => {
        setSaving(true);
        setErr(null);
        setNotice(null);
        const { httpStatus, body: r } = await writeResult(bp_put_privacy_settings(draft));
        if (r.status !== 1) {
            setErr(r.message || r.error
                || (httpStatus === 403
                    ? 'Only organization admins can change privacy settings.'
                    : 'Save failed.'));
        } else { setNotice('Privacy settings saved.'); load(); }
        setSaving(false);
    };

    if (!cfg || !draft) return (
        <div className="bm_prv_loading"><CommonLoader label="Loading privacy policy..." /></div>
    );

    const setField = (patch) => setDraft({ ...draft, ...patch });
    const setPii = (key, visible) => setDraft({
        ...draft,
        pii_visibility: { ...draft.pii_visibility, [key]: visible },
    });

    return (
        <div className="bm_prv">
            {err && (
                <div className="bm_prv_alert bm_prv_alert--error" role="alert">
                    <StatusBadge variant="danger">Error</StatusBadge>
                    <Body as="span" variant={2}>{err}</Body>
                </div>
            )}
            {notice && (
                <div className="bm_prv_alert bm_prv_alert--success" role="status">
                    <StatusBadge variant="success">Saved</StatusBadge>
                    <Body as="span" variant={2}>{notice}</Body>
                </div>
            )}

            <div className="bm_prv_kpis">
                <StatCard className="cmn_elevated_card"
                    label="Profile pictures"
                    value={cfg.show_avatar ? 'Visible' : 'Hidden'}
                    hint={cfg.show_avatar ? 'Real avatars shown' : 'Platform default shown'}
                    icon={<span className={`bm_kpi_icon is-${cfg.show_avatar ? 'neutral' : 'success'}`}><ModuleIcon name="easm" size={18} color="currentColor" /></span>}
                    iconLeft />
                <StatCard className="cmn_elevated_card"
                    label="Author names"
                    value={cfg.show_name ? 'Visible' : 'Masked'}
                    hint={cfg.show_name ? 'Real handles shown' : 'Names replaced with mask'}
                    icon={<span className={`bm_kpi_icon is-${cfg.show_name ? 'neutral' : 'success'}`}><ModuleIcon name="compliance" size={18} color="currentColor" /></span>}
                    iconLeft />
                <StatCard className="cmn_elevated_card"
                    label="Post links"
                    value={cfg.show_source_url ? 'Visible' : 'Hidden'}
                    hint={cfg.show_source_url ? 'Original URLs shown' : 'Links redacted'}
                    icon={<span className={`bm_kpi_icon is-${cfg.show_source_url ? 'neutral' : 'success'}`}><ModuleIcon name="check_circle" size={18} color="currentColor" /></span>}
                    iconLeft />
                <StatCard className="cmn_elevated_card"
                    label="PII revealed"
                    value={`${revealedCount} of ${piiTypes.length}`}
                    hint={revealedCount === 0 ? 'All PII redacted' : `${revealedCount} type${revealedCount === 1 ? '' : 's'} visible`}
                    icon={<span className={`bm_kpi_icon is-${revealedCount > 0 ? 'warning' : 'success'}`}><ModuleIcon name="risk" size={18} color="currentColor" /></span>}
                    iconLeft />
            </div>

            <Panel title="Author identity" note="Controls what the mention feeds and exports reveal about who posted">
                <ToggleRow
                    label="Profile pictures"
                    description="Redacted serves the platform's generic disc instead of the account's real photo."
                    value={draft.show_avatar}
                    onChange={(v) => setField({ show_avatar: v })}
                    disabled={!canManage}
                />
                <ToggleRow
                    label="Author names and handles"
                    description="Redacted replaces the handle using the mask style below. Masked names stay consistent per author."
                    value={draft.show_name}
                    onChange={(v) => setField({ show_name: v })}
                    disabled={!canManage}
                />
                {!draft.show_name && (cfg.available_name_mask_modes || []).length > 0 && (
                    <div className="bm_prv_mask_row">
                        <Body as="span" variant={2} className="bm_prv_muted">Mask style</Body>
                        <CommonDropdown
                            ariaLabel="Name mask style"
                            value={draft.name_mask_mode}
                            onChange={(v) => setField({ name_mask_mode: v })}
                            options={(cfg.available_name_mask_modes || [])
                                .map(m => ({ value: m.value, label: m.label }))}
                            disabled={!canManage}
                            minWidth={280}
                        />
                    </div>
                )}
                <ToggleRow
                    label="Post links"
                    description="A post URL usually contains the handle, so links can re-identify a masked author. Redacting them gives full anonymity; takedowns keep working either way."
                    value={draft.show_source_url}
                    onChange={(v) => setField({ show_source_url: v })}
                    disabled={!canManage}
                    isLast
                />
            </Panel>

            {piiTypes.length > 0 && (
                <Panel
                    title="Personal information in post text"
                    note={`${revealedCount ? `${revealedCount} of ${piiTypes.length} revealed` : 'All redacted'} - every type is redacted by default`}>
                    {piiTypes.map((t, i) => (
                        <ToggleRow
                            key={t.key}
                            label={t.label}
                            description={t.description}
                            value={draft.pii_visibility?.[t.key] === true}
                            onChange={(v) => setPii(t.key, v)}
                            disabled={!canManage}
                            isLast={i === piiTypes.length - 1}
                        />
                    ))}
                </Panel>
            )}

            <div className="bm_prv_footer">
                <CommonButton
                    text={saving ? 'Saving...' : 'Save privacy settings'}
                    variant="primary" className="bm_scan_btn"
                    onClick={save}
                    disable={!canManage || saving || !dirty}
                />
                {!canManage && (
                    <Body as="span" variant={2} className="bm_prv_readonly">
                        Read-only. Only organization admins can change privacy settings.
                    </Body>
                )}
                {cfg.updated_at && (
                    <Body as="span" variant={2} className="bm_prv_muted">
                        Last changed {formatLocalDateTime(cfg.updated_at)}
                        {cfg.updated_by && <> by {cfg.updated_by}</>}
                    </Body>
                )}
            </div>
        </div>
    );
}
